
import React, { useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { UserRound, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { convertHeicToJpeg, isHeicFile } from "@/utils/heicConvert";
import { captureException } from "@/lib/sentry";

interface ProfilePictureUploadProps {
  profilePicture?: File;
  onUpdate: (file: File) => void;
}

const ProfilePictureUpload = ({
  profilePicture,
  onUpdate
}: ProfilePictureUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isConverting, setIsConverting] = useState(false);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0];
    if (!file) return;

    // iPhone photos default to HEIC; convert to JPEG so storage + <img> render it
    if (isHeicFile(file)) {
      setIsConverting(true);
      try {
        file = await convertHeicToJpeg(file);
      } catch (err) {
        console.error('[ProfilePictureUpload] HEIC conversion failed:', err);
        captureException(err instanceof Error ? err : new Error(String(err)), {
          where: 'ProfilePictureUpload.handleImageChange',
          fileName: file?.name,
          fileSize: file?.size,
          fileType: file?.type,
        });
        toast({
          title: "Couldn't read that photo",
          description: "Try saving it as JPEG and uploading again.",
          variant: "destructive",
        });
        setIsConverting(false);
        return;
      }
      setIsConverting(false);
    }

    if (!file.type.includes("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PNG or JPG image.",
        variant: "destructive",
      });
      return;
    }

    onUpdate(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="relative w-32 h-32">
        {profilePicture ? (
          <div className="w-full h-full rounded-full overflow-hidden border-2 border-brand-primary/20">
            <img
              src={URL.createObjectURL(profilePicture)}
              alt="Profile Preview"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-full h-full rounded-full bg-brand-primary/10 border-2 border-dashed border-brand-primary/20 flex items-center justify-center">
            <UserRound className="h-16 w-16 text-brand-primary/50" />
          </div>
        )}
      </div>
      
      <div className="w-full max-w-xs">
        <Label htmlFor="profile" className="sr-only">Profile Picture</Label>
        <Input
          ref={fileInputRef}
          id="profile"
          type="file"
          accept=".jpg,.jpeg,.png,.heic,.heif,image/*"
          onChange={handleImageChange}
          className="hidden"
        />

        <Button
          type="button"
          onClick={handleButtonClick}
          disabled={isConverting}
          className="w-full flex items-center justify-center gap-2 bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 border border-brand-primary/20"
        >
          {isConverting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Converting your photo…</span>
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              <span>{profilePicture ? "Change Photo" : "Upload Photo"}</span>
            </>
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground mt-2">
          JPG, PNG, or HEIC, max 5MB
        </p>
      </div>
    </div>
  );
};

export default ProfilePictureUpload;
